import React, { FC } from 'react';
import { Location } from 'history';
import { useSelector } from 'react-redux';

import { type ImmutableTrip, type Trip } from 'src/types/trips/Trips';
import { getOffer, getTransaction } from 'src/common/utils/reduxStoreSelectors';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import CalendarAvailabilityManager from 'src/common/components/UpdateAvailability';
import useBoats from 'src/common/hooks/useBoats';
import {
  CaptainCard,
  DurationCard,
  DatesCard,
  TimeCard,
  GroupSizeCard,
  CommentCard,
  VesselCard,
  DirectBookingCard,
} from '../../cards';
import CTA from '../../CTA';
import OwnerPriceSection from '../../PriceSection/owner';
import TripPanel from '../../presentation/TripPanel';
import TripDetailsTitle from '../../presentation/TripDetailsTitle';
import content, { getMessage } from '../content';
import TopScroll from '../../../../common/components/TopScroll';
import { PATHS } from '../../../../common/constants';
import useCustomOfferExpiry from '../../../../common/hooks/useCustomOfferExpiry';
import { preserveSearch } from '../../../../common/utils/routing';
import { CAPTAINED_FIELD, TRIP_ENDS_AT_FIELD, TRIP_STARTS_AT_FIELD } from '../../../constants';
import OfferSummary from '../../OfferSummary';
import TripMenu from '../../TripMenu';
import DateUnavailable from '../../DateUnavailable';
import TripPaymentLink from '../../TripPaymentLink';

type LocationState = {
  initialStartDate?: Trip[typeof TRIP_STARTS_AT_FIELD];
  initialEndDate?: Trip[typeof TRIP_ENDS_AT_FIELD];
  shouldActivateUpdateAvailabilityFlow?: boolean;
};

type OfferSentProps = {
  cancellationDropdownOptions?: DropdownOption[];
  trip: ImmutableTrip;
  location: Location & { state?: LocationState };
};

const OfferSent: FC<OfferSentProps> = ({
  cancellationDropdownOptions,
  trip,
  location,
}) => {
  const transaction = useSelector(getTransaction).toJS();
  const amounts = transaction?.amounts || trip?.getIn(['transaction', 'amounts'])?.toJS();
  const tripState = trip.getIn(['state', 'state']);
  const captained = trip.get(CAPTAINED_FIELD);
  const { renderCustomExpiry } = useCustomOfferExpiry(trip);
  const isDirectBooking = trip.get('direct_booking', false);
  const directBookingUrl = trip.get('direct_booking_url', '');
  const offer = useSelector(getOffer);
  const boat = (offer.get('boat') || trip.get('boat')).toJS();
  const { boatsIndexedById } = useBoats();
  const numericId = boat && boatsIndexedById[boat.id]?.boat_id;
  const { state } = location;
  const cta = (
    <CTA classNameModifier="withSidebar">
      <TripMenu
        cancellationDropdownOptions={cancellationDropdownOptions}
        trip={trip}
      />
    </CTA>
  );

  return (
    <TripPanel
      cta={cta}
      trip={trip}
      subheader={content.title[tripState]}
      intro={getMessage(trip, location)}
      backLocation={preserveSearch(PATHS.INBOX, location)}
      classNameModifier="withoutSideMargins"
    >
      <TopScroll />
      <DateUnavailable trip={trip} />

      {isDirectBooking
        && (
          <>
            {<TripPaymentLink textLink={directBookingUrl as string} />}
            <DirectBookingCard trip={trip} />
          </>
        )}
      <OwnerPriceSection
        amounts={amounts}
        readOnly
        renderCustomExpiry={renderCustomExpiry}
        trip={trip}
      >
        <OfferSummary amounts={amounts} />
      </OwnerPriceSection>
      <TripDetailsTitle />
      <VesselCard
        checkTripModification
        trip={trip}
        readOnly
      />
      <DatesCard
        checkTripModification
        trip={trip}
        readOnly
      />
      {/* TODO fix TS error of TimeCard */}
      <TimeCard
        checkTripModification
        trip={trip}
        readOnly
      />
      <DurationCard
        checkTripModification
        trip={trip}
      />
      <GroupSizeCard
        checkTripModification
        trip={trip}
        readOnly
      />
      {captained !== null && (
        <CaptainCard
          checkTripModification
          trip={trip}
          readOnly
        />
      )}
      <CommentCard
        checkTripModification
        trip={trip}
        readOnly
      />
      {state?.shouldActivateUpdateAvailabilityFlow
        && state?.initialStartDate
        && state?.initialEndDate
      && (
        <CalendarAvailabilityManager
          boat={boat}
          boatId={numericId}
          endDateString={state.initialEndDate}
          startDateString={state.initialStartDate}
          showPrompt={state.shouldActivateUpdateAvailabilityFlow}
        />
      )}
    </TripPanel>
  );
};

export default OfferSent;
